import React, { useLayoutEffect, useRef, useState } from 'react';
import { useIsMobileResolution } from '@philips-internal/oswald-ui-components';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { bool, element, eventListener, expression } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { trigger } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as Edit } from '../../../resources/assets/edit-pencil.svg';
import { foundationContext } from '../../context/index.js';
import { ModuleSelector, ModuleSelectorType } from '../module-selector/index.js';
import { EditInformation } from './edit-information.js';
import { SpotlightAPI } from './spotlight-api.js';
import { SPOTLIGHT_PARENT_SLIDEBAR_SELECTOR } from './spotlight-constants.js';
import { UserMenu } from './user-menu/index.js';
import './spotlight.css';
const awesomeProps = {
    api: SpotlightAPI,
    type: ComponentType.LAYOUT,
};
const renderLocationContent = (title, text) => {
    return (React.createElement("div", { className: "c-spotlight__sector u-truncate" },
        React.createElement("span", { className: "c-spotlight__location-title" }, MessageService.translate(title)),
        React.createElement("span", { className: "c-spotlight__location-text" }, text)));
};
const renderLocation = (props, onEditClick) => {
    const establishment = foundationContext.getEstablishment();
    const profile = foundationContext.getProfile();
    const sector = foundationContext.getSector();
    const canEditEstablishment = foundationContext.canEditEstablishment();
    const canEditProfile = foundationContext.canEditProfile();
    const canEditSector = foundationContext.canEditSector();
    const showEstablishment = establishment && foundationContext.canShowEstablishment();
    const showProfile = profile && foundationContext.canShowProfile();
    const showSector = sector && foundationContext.canShowSector();
    const showEdit = canEditEstablishment || canEditProfile || canEditSector;
    return (React.createElement("div", { className: "c-spotlight__section c-spotlight__location" },
        React.createElement("div", { className: "c-spotlight__location-texts" },
            showEstablishment ? renderLocationContent(props.establishmentExpression, establishment?.name) : null,
            showProfile ? renderLocationContent(props.profileExpression, profile?.name) : null,
            showSector ? renderLocationContent(props.sectorExpression, sector?.name) : null),
        showEdit ? React.createElement(Edit, { className: "c-spotlight__location-edit", onClick: onEditClick }) : null));
};
const renderApplicationTitle = (props) => {
    if (props.hideTitle || !props.applicationTitle) {
        return null;
    }
    return (React.createElement("div", { className: "c-spotlight__title" }, MessageService.translate(props.applicationTitle)));
};
function Spotlight(props) {
    const [showEditModal, setShowEditModal] = useState(false);
    const isMobileResolution = useIsMobileResolution();
    const spotlightRef = useRef(null);
    const [hasSlidebarAsParent, setHasSlidebarAsParent] = useState(false);
    useLayoutEffect(() => {
        if (!spotlightRef.current) {
            return;
        }
        const slidebar = spotlightRef.current.closest(SPOTLIGHT_PARENT_SLIDEBAR_SELECTOR);
        setHasSlidebarAsParent(Boolean(slidebar?.contains(spotlightRef.current)));
    }, [spotlightRef.current]);
    return (React.createElement("div", { "aria-label": MessageService.translate('tws.spotlight.label'), className: "c-spotlight", ref: spotlightRef, role: "menu" },
        renderApplicationTitle(props),
        React.createElement(UserMenu, { applicationTitle: props.applicationTitle, externalFragment: foundationContext.getMenuFragment(), location: renderLocation(props, () => setShowEditModal(true)), logoutExpression: props.logoutExpression, onClick: props.onClick, showSubMenuHeader: props.subMenuHeader }),
        isMobileResolution || hasSlidebarAsParent ? renderLocation(props, trigger(setShowEditModal, true)) : null,
        React.createElement(ModuleSelector, { moduleType: ModuleSelectorType.SPOTLIGHT, onClose: props.onClose, showIconsOnSpatialDesktop: props.showIconsOnSpatialDesktop }),
        showEditModal
            ? React.createElement(EditInformation, { establishmentExpression: props.establishmentExpression, modalTitle: props.editInformationExpression, onClose: () => setShowEditModal(false), profileExpression: props.profileExpression, sectorExpression: props.sectorExpression, submitExpression: props.submitExpression })
            : null));
}
Spotlight.propTypes = {
    applicationTitle: expression,
    customHeader: element,
    editInformationExpression: expression,
    establishmentExpression: expression,
    hideSearch: bool,
    hideTitle: bool,
    logoutExpression: expression,
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    profileExpression: expression,
    searchExpression: expression,
    sectorExpression: expression,
    showIconsOnSpatialDesktop: bool,
    subMenuHeader: bool,
    submitExpression: expression,
};
Spotlight.defaultProps = {
    applicationTitle: 'application.name',
    editInformationExpression: 'tws.spotlight.edit-information',
    establishmentExpression: 'tws.spotlight.establishment',
    hideSearch: false,
    hideTitle: false,
    logoutExpression: 'tws.spotlight.logout',
    profileExpression: 'tws.spotlight.profile',
    searchExpression: 'tws.spotlight.search',
    sectorExpression: 'tws.spotlight.sector',
    showIconsOnSpatialDesktop: true,
    subMenuHeader: false,
    submitExpression: 'tws.spotlight.submit',
};
AwesomeComponent(awesomeProps)(Spotlight);
export { Spotlight, };
//# sourceMappingURL=spotlight.js.map